import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical, faTag, faPen } from '@fortawesome/pro-light-svg-icons';
import { ManageStatusesModal, ManageStatusesModalRef } from '../modals/ManageStatusesModal';
import { ManageTagsModal, ManageTagsModalRef } from '../modals/ManageTagsModal';
import { useCanManageCRM } from '@modules/CRM/shared/hooks/useCRMSettings';

const BreadcrumbComponent: FC = () => {
  const { t } = useTranslation('crmModule');
  const manageStatusesModalRef = useRef<ManageStatusesModalRef>(null);
  const manageTagsModalRef = useRef<ManageTagsModalRef>(null);
  const { canManage } = useCanManageCRM();

  const breadcrumbs = useMemo(
    () => [{ label: t('breadcrumb.crm', 'CRM'), path: '/orders/crm' }, { label: t('breadcrumb.orders', 'Órdenes') }],
    [t],
  );

  usePageNavigation({ breadcrumbs });

  const handleOpenStatuses = () => {
    manageStatusesModalRef.current?.open();
  };

  const handleOpenTags = () => {
    manageTagsModalRef.current?.open();
  };

  return (
    <>
      <div className="fixed top-4 right-6 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center justify-center rounded-md hover:bg-hover-press transition-colors bg-transparent border-0"
              data-testid="crm-management-menu"
              aria-label={t('management.menu', 'Menú de gestión')}
            >
              <FontAwesomeIcon icon={faEllipsisVertical} className="w-4 h-4 text-icons" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[180px]">
            <DropdownMenuItem
              onClick={handleOpenStatuses}
              disabled={!canManage}
              className="cursor-pointer"
              data-testid="menu-item-statuses"
            >
              <FontAwesomeIcon icon={faPen} className="mr-2 w-3.5 h-3.5 text-icons" />
              <span>{t('management.statuses', 'Estados')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleOpenTags}
              disabled={!canManage}
              className="cursor-pointer"
              data-testid="menu-item-tags"
            >
              <FontAwesomeIcon icon={faTag} className="mr-2 w-3.5 h-3.5 text-icons" />
              <span>{t('management.tags', 'Etiquetas')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <ManageStatusesModal ref={manageStatusesModalRef} />
      <ManageTagsModal ref={manageTagsModalRef} />
    </>
  );
};

BreadcrumbComponent.displayName = 'Breadcrumb';

export { BreadcrumbComponent as Breadcrumb };
